import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Maximize, Grid, ChevronLeft, ChevronRight } from 'lucide-react';
// Import all slides
import { SlideCover } from './components/slides/SlideCover';
import { SlideProblem } from './components/slides/SlideProblem';
import { SlideWhyNow } from './components/slides/SlideWhyNow';
import { SlideInsight } from './components/slides/SlideInsight';
import { SlideSolution } from './components/slides/SlideSolution';
import { SlideProduct } from './components/slides/SlideProduct';
import { SlideBrain } from './components/slides/SlideBrain';
import { SlideHowItWorks } from './components/slides/SlideHowItWorks';
import { SlideTraction } from './components/slides/SlideTraction';
import { SlideCustomers } from './components/slides/SlideCustomers';
import { SlideRecognition } from './components/slides/SlideRecognition';
import { SlideMarket } from './components/slides/SlideMarket';
import { SlideTeam } from './components/slides/SlideTeam';
import { SlideClose } from './components/slides/SlideClose';
import { SlideSandbox } from './components/slides/SlideSandbox';
import { PasswordGate } from './components/PasswordGate';
const SLIDES = [{
  id: 'cover',
  component: SlideCover
}, {
  id: 'problem',
  component: SlideProblem
}, {
  id: 'whynow',
  component: SlideWhyNow
}, {
  id: 'insight',
  component: SlideInsight
}, {
  id: 'solution',
  component: SlideSolution
}, {
  id: 'product',
  component: SlideProduct
}, {
  id: 'brain',
  component: SlideBrain
}, {
  id: 'howitworks',
  component: SlideHowItWorks
}, {
  id: 'traction',
  component: SlideTraction
}, {
  id: 'customers',
  component: SlideCustomers
}, {
  id: 'recognition',
  component: SlideRecognition
}, {
  id: 'market',
  component: SlideMarket
}, {
  id: 'team',
  component: SlideTeam
}, {
  id: 'close',
  component: SlideClose
}, {
  id: 'sandbox',
  component: SlideSandbox
}];
function Deck() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [gridView, setGridView] = useState(false);
  const nextSlide = useCallback(() => {
    setCurrentIndex(prev => Math.min(prev + 1, SLIDES.length - 1));
  }, []);
  const prevSlide = useCallback(() => {
    setCurrentIndex(prev => Math.max(prev - 1, 0));
  }, []);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (gridView) {
        if (e.key === 'Escape' || e.key === 'g') setGridView(false);
        return;
      }
      switch (e.key) {
        case 'ArrowRight':
        case 'Space':
        case ' ':
          e.preventDefault();
          nextSlide();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          prevSlide();
          break;
        case 'g':
          setGridView(true);
          break;
        case 'f':
          if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen().catch(() => {});
          } else {
            document.exitFullscreen().catch(() => {});
          }
          break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide, gridView]);
  // Horizontal trackpad / two-finger swipe navigation (Mac, Magic Mouse, touchpads)
  const swipeLockRef = useRef(false);
  const swipeAccumRef = useRef(0);
  const swipeTimeoutRef = useRef<number | null>(null);
  useEffect(() => {
    if (gridView) return;
    const HORIZONTAL_THRESHOLD = 60; // accumulated px before triggering
    const LOCK_DURATION = 700; // ms cooldown so one gesture = one navigation
    const handleWheel = (e: WheelEvent) => {
      // Ignore if target is inside an interactive element (e.g. email input on sandbox slide)
      const target = e.target as HTMLElement | null;
      if (target && target.closest('input, textarea, select, button[type="submit"]')) {
        return;
      }
      const absX = Math.abs(e.deltaX);
      const absY = Math.abs(e.deltaY);
      // Only act on horizontal-dominant scroll
      if (absX <= absY || absX < 4) {
        return;
      }
      e.preventDefault();
      if (swipeLockRef.current) return;
      swipeAccumRef.current += e.deltaX;
      // Reset accumulator if user pauses mid-gesture
      if (swipeTimeoutRef.current) window.clearTimeout(swipeTimeoutRef.current);
      swipeTimeoutRef.current = window.setTimeout(() => {
        swipeAccumRef.current = 0;
      }, 180);
      if (Math.abs(swipeAccumRef.current) >= HORIZONTAL_THRESHOLD) {
        if (swipeAccumRef.current > 0) {
          nextSlide();
        } else {
          prevSlide();
        }
        swipeAccumRef.current = 0;
        swipeLockRef.current = true;
        window.setTimeout(() => {
          swipeLockRef.current = false;
        }, LOCK_DURATION);
      }
    };
    window.addEventListener('wheel', handleWheel, {
      passive: false
    });
    return () => {
      window.removeEventListener('wheel', handleWheel);
      if (swipeTimeoutRef.current) window.clearTimeout(swipeTimeoutRef.current);
    };
  }, [nextSlide, prevSlide, gridView]);
  // Touch swipe for phones/tablets
  useEffect(() => {
    if (gridView) return;
    let touchStartX = 0;
    let touchStartY = 0;
    let touchStartTime = 0;
    const handleTouchStart = (e: TouchEvent) => {
      const t = e.touches[0];
      touchStartX = t.clientX;
      touchStartY = t.clientY;
      touchStartTime = Date.now();
    };
    const handleTouchEnd = (e: TouchEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && target.closest('input, textarea, select')) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - touchStartX;
      const dy = t.clientY - touchStartY;
      const dt = Date.now() - touchStartTime;
      // Must be a quick, horizontal-dominant swipe
      if (dt > 600) return;
      if (Math.abs(dx) < 50) return;
      if (Math.abs(dx) <= Math.abs(dy)) return;
      if (dx < 0) nextSlide();else prevSlide();
    };
    window.addEventListener('touchstart', handleTouchStart, {
      passive: true
    });
    window.addEventListener('touchend', handleTouchEnd, {
      passive: true
    });
    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [nextSlide, prevSlide, gridView]);
  const CurrentSlideComponent = SLIDES[currentIndex].component;
  const progress = (currentIndex + 1) / SLIDES.length * 100;
  if (gridView) {
    return <div className="min-h-screen bg-black p-8 overflow-y-auto" data-id="element-0">
        <div className="flex justify-between items-center mb-8 text-white" data-id="element-1">
          <h1 className="text-2xl font-serif" data-id="element-2">Deck Overview</h1>
          <button onClick={() => setGridView(false)} className="text-sm uppercase tracking-widest hover:text-gray-400 transition-colors" data-id="element-3">
            Close Grid (Esc)
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-id="element-4">
          {SLIDES.map((slide, idx) => <div key={slide.id} onClick={() => {
          setCurrentIndex(idx);
          setGridView(false);
        }} className={`aspect-video relative cursor-pointer border transition-all overflow-hidden ${idx === currentIndex ? 'border-white scale-[1.02] z-10 shadow-2xl shadow-white/10' : 'border-gray-800 hover:border-gray-500'}`} data-id="element-5">
              <div className="absolute inset-0 pointer-events-none scale-[0.2] origin-top-left w-[500%] h-[500%]" data-id="element-6">
                <slide.component data-id="element-7" />
              </div>
              <div className="absolute bottom-2 right-2 text-xs text-white bg-black/80 px-2 py-1 rounded backdrop-blur-sm border border-gray-800" data-id="element-8">
                {idx + 1}
              </div>
            </div>)}
        </div>
      </div>;
  }
  return <div className="relative w-full h-screen bg-black overflow-hidden flex flex-col" data-id="element-9">
      {/* Progress Bar */}
      <div className="absolute top-0 left-0 w-full h-1 z-50 bg-gray-900" data-id="element-10">
        <motion.div className="h-full bg-white" initial={{
        width: 0
      }} animate={{
        width: `${progress}%`
      }} transition={{
        duration: 0.3
      }} data-id="element-11" />
      </div>

      {/* Main Slide Area */}
      <div className="flex-1 relative" data-id="element-12">
        <AnimatePresence mode="wait" data-id="element-13">
          <motion.div key={currentIndex} className="absolute inset-0" data-id="element-14">
            <CurrentSlideComponent data-id="element-15" />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Clickable Navigation Zones (subtle hit areas on desktop) */}
      <div className="absolute inset-y-0 left-0 w-1/6 z-30 hidden md:block cursor-w-resize" onClick={prevSlide} aria-hidden data-id="element-16" />
      <div className="absolute inset-y-0 right-0 w-1/6 z-30 hidden md:block cursor-e-resize" onClick={nextSlide} aria-hidden data-id="element-17" />

      {/* Visible Arrow Navigation Buttons */}
      <button onClick={prevSlide} disabled={currentIndex === 0} aria-label="Previous slide" className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-50 mix-blend-difference text-white p-3 md:p-4 border border-white/30 rounded-full hover:border-white hover:bg-white/10 active:scale-95 transition-all disabled:opacity-20 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:border-white/30" data-id="element-18">
        <ChevronLeft size={20} strokeWidth={1.5} data-id="element-19" />
      </button>
      <button onClick={nextSlide} disabled={currentIndex === SLIDES.length - 1} aria-label="Next slide" className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-50 mix-blend-difference text-white p-3 md:p-4 border border-white/30 rounded-full hover:border-white hover:bg-white/10 active:scale-95 transition-all disabled:opacity-20 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:border-white/30" data-id="element-20">
        <ChevronRight size={20} strokeWidth={1.5} data-id="element-21" />
      </button>

      {/* Footer Controls */}
      <div className="absolute bottom-6 left-6 right-6 z-50 flex justify-between items-end pointer-events-none mix-blend-difference text-white" data-id="element-22">
        <div className="font-mono text-sm opacity-50" data-id="element-23">
          {String(currentIndex + 1).padStart(2, '0')} / {SLIDES.length}
        </div>

        <div className="flex gap-4 pointer-events-auto opacity-50 hover:opacity-100 transition-opacity" data-id="element-24">
          <button onClick={() => setGridView(true)} className="hover:text-white transition-colors p-2" title="Grid View (G)" data-id="element-25">
            <Grid size={16} data-id="element-26" />
          </button>
          <button onClick={() => {
          if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen().catch(() => {});
          } else {
            document.exitFullscreen().catch(() => {});
          }
        }} className="hover:text-white transition-colors p-2" title="Fullscreen (F)" data-id="element-27">
            <Maximize size={16} data-id="element-28" />
          </button>
        </div>
      </div>
    </div>;
}
export function App() {
  return <PasswordGate data-id="element-29">
      <Deck data-id="element-30" />
    </PasswordGate>;
}